import { Alert, Button, CircularProgress, Collapse, Snackbar } from '@mui/material';
import React, { ReactNode, useRef } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompleteIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingIcon from '@mui/icons-material/PendingActions';
import SyncIcon from '@mui/icons-material/Sync';
import MailIcon from '@mui/icons-material/MarkEmailRead';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SettingsIcon from '@mui/icons-material/Tune';
import FirmIcon from '@mui/icons-material/Groups';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import { MessageAction, SuccessIcon } from '../MessageContext';
import { withEllipsis } from '../util/StringUtil';

const SUCCESS_ICONS: Record<SuccessIcon, ReactNode> = {
  complete: <CompleteIcon />,
  pending: <PendingIcon />,
  sync: <SyncIcon />,
  email: <MailIcon />,
  undo: <UndoIcon />,
  save: <SaveIcon />,
  person: <PersonIcon />,
  personAdd: <PersonAddIcon />,
  personRemove: <PersonRemoveIcon />,
  settings: <SettingsIcon />,
  firm: <FirmIcon />,
  lockOpen: <LockOpenIcon />,
  lock: <LockIcon />,
};

type MessagePanelProps = {
  successMessage?: string | ReactNode | null;
  successAction?: MessageAction;
  successIcon?: SuccessIcon;
  setSuccessMessage?: Function;
  setErrorMessage: Function;
  errorMessage: string | ReactNode | null;
  progressMessage?: string | ReactNode | null;
};

/**
 * Errors and progress stay inline above the page, so they can't be missed.
 * Success messages show as a pastel green toast that closes itself and doesn't push the page down.
 */
export default function MessagePanel(props: MessagePanelProps) {
  const { errorMessage, successMessage, successAction, successIcon, progressMessage, setErrorMessage, setSuccessMessage } = props;
  const closeSuccess = () => setSuccessMessage && setSuccessMessage(null);

  // The toast takes a moment to fade out after it's closed. Keep showing the last message meanwhile,
  // otherwise it fades out as an empty green box with the default tick.
  const lastSuccess = useRef({ message: successMessage, action: successAction, icon: successIcon });
  if (successMessage != null) {
    lastSuccess.current = { message: successMessage, action: successAction, icon: successIcon };
  }
  const shown = lastSuccess.current;

  // A new success message gets a new toast, so it pops in again instead of only changing the text of the previous one
  // (e.g. marking clients as complete, then as pending).
  const toastKey = useRef(0);
  const wasShowingSuccess = useRef(false);
  if (successMessage != null && !wasShowingSuccess.current) {
    toastKey.current++;
  }
  wasShowingSuccess.current = successMessage != null;

  // Same as the toast: keep the last progress message while the banner collapses, otherwise a quick action
  // collapses it as an empty blue box.
  const lastProgress = useRef(progressMessage);
  if (progressMessage != null) {
    lastProgress.current = progressMessage;
  }

  return (
    <>
      <Collapse in={errorMessage != null}>
        <Alert onClose={() => setErrorMessage(null)} severity="error" style={{ marginBottom: '4px' }}>
          {errorMessage}
        </Alert>
      </Collapse>

      <Snackbar
        key={toastKey.current}
        open={successMessage != null}
        // Leave more time when there's something to click, like Undo. MUI pauses the timer while hovered.
        autoHideDuration={shown.action ? 10000 : 5000}
        onClose={(_, reason) => reason !== 'clickaway' && closeSuccess()}
        // Centred so it doesn't cover Help and Logout at the bottom of the side nav
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          role="status"
          icon={SUCCESS_ICONS[shown.icon as SuccessIcon] || <CheckCircleIcon />}
          onClose={closeSuccess}
          // Pastel green, the same tint as the Complete status chip
          sx={{
            alignItems: 'center',
            boxShadow: 3,
            bgcolor: '#e9f3ea',
            color: '#1e4620',
            border: '1px solid #6aa86d',
            '& .MuiAlert-icon': { color: '#3a7a3e' },
          }}
          action={
            shown.action && (
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                sx={{ fontWeight: 600 }}
                onClick={() => {
                  closeSuccess();
                  shown.action?.onClick();
                }}
              >
                {shown.action.label}
              </Button>
            )
          }
        >
          {shown.message}
        </Alert>
      </Snackbar>

      <Collapse in={progressMessage != null}>
        <Alert severity="info" icon={<CircularProgress size={20} />} style={{ marginBottom: '4px' }}>
          {withEllipsis(lastProgress.current)}
        </Alert>
      </Collapse>
    </>
  );
}
